import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  useConversationScript,
  useConversationScripts,
  useActivateScript,
  useArchiveScript,
  useScriptDeployments,
} from "@/hooks/useConversationScripts";

const STATUS_LABEL: Record<string, string> = {
  draft: "Borrador",
  active: "Activo",
  archived: "Archivado",
};

const STATUS_COLOR: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-800",
  active: "bg-green-100 text-green-800",
  archived: "bg-ink-100 text-ink-500",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ScriptEditor(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: script, isPending, isError } = useConversationScript(id);
  const { data: allScripts } = useConversationScripts();
  const { data: deployments } = useScriptDeployments(id);
  const activateMutation = useActivateScript(id!);
  const archiveMutation = useArchiveScript(id!);

  // Sibling versions of this script (same useCase), newest first.
  const versions = script
    ? (allScripts ?? [])
        .filter((s) => s.useCase === script.useCase)
        .sort((a, b) => b.version - a.version)
    : [];

  function handleArchive(): void {
    archiveMutation.mutate(undefined, {
      onSuccess: () => toast.success("Script archivado."),
      onError: (err) => toast.error(err.message ?? "Error al archivar."),
    });
  }

  // Inverse of archive — restores an archived version to "active" (deployable again).
  function handleUnarchive(): void {
    activateMutation.mutate(undefined, {
      onSuccess: () => toast.success("Script desarchivado."),
      onError: (err) => toast.error(err.message ?? "Error al desarchivar."),
    });
  }

  if (isPending) return <div className="p-6 text-sm text-ink-500">Cargando…</div>;
  if (isError || !script) return <div className="p-6 text-sm text-red-500">Error al cargar script.</div>;

  const isArchived = script.status === "archived";
  const isBusy = activateMutation.isPending || archiveMutation.isPending;

  // A version pinned by an active deployment cannot be archived — it would keep
  // powering a live client. The server enforces this too (409); we disable the
  // button so the block is obvious before the click.
  const versionInUse = (deployments ?? []).some((d) => d.scriptVersion === script.version);

  const jsonText = JSON.stringify(script.parsed, null, 2);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void navigate("/super-admin/scripts")}
            className="text-sm text-ink-500 hover:text-ink-900"
          >
            ← Scripts
          </button>
          <span className="text-ink-200">/</span>
          <h1 className="text-lg font-semibold text-ink-900">{script.name}</h1>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[script.status]}`}
          >
            {STATUS_LABEL[script.status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isArchived && (
            <button
              type="button"
              disabled={isBusy || versionInUse}
              onClick={handleArchive}
              title={
                versionInUse
                  ? "En uso por un deployment activo — cámbialo a otra versión antes de archivar"
                  : undefined
              }
              className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-50"
            >
              {archiveMutation.isPending ? "Archivando…" : "Archivar"}
            </button>
          )}
          {isArchived && (
            <button
              type="button"
              disabled={isBusy}
              onClick={handleUnarchive}
              className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {activateMutation.isPending ? "Desarchivando…" : "Desarchivar"}
            </button>
          )}
        </div>
      </div>

      {/* Body — read-only content + metadata */}
      <div className="flex min-h-0 flex-1">
        {/* JSON viewer (read-only) */}
        <div className="flex min-w-0 flex-1 flex-col border-r border-ink-100">
          <textarea
            value={jsonText}
            readOnly
            spellCheck={false}
            className="min-h-0 flex-1 resize-none bg-ink-050 p-4 font-mono text-sm text-ink-800 focus:outline-none"
          />
          <div className="border-t border-ink-100 bg-ink-050 px-4 py-2">
            <p className="text-xs text-ink-500">
              Contenido de solo lectura — los scripts se versionan en el repositorio y se
              sincronizan al desplegar.
            </p>
          </div>
        </div>

        {/* Metadata panel */}
        <div className="w-64 flex-shrink-0 overflow-y-auto bg-white p-5">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-ink-500">
            Metadatos
          </h2>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs text-ink-400">Versión</dt>
              <dd>
                <select
                  value={script.id}
                  onChange={(e) => void navigate(`/super-admin/scripts/${e.target.value}`)}
                  className="w-full rounded border border-ink-200 px-2 py-1 text-sm font-medium text-ink-900 focus:border-moss focus:outline-none"
                >
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version} ({STATUS_LABEL[v.status]})
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Estado</dt>
              <dd>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[script.status]}`}
                >
                  {STATUS_LABEL[script.status]}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Caso de uso</dt>
              <dd className="font-medium text-ink-900">{script.useCase}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Tipo de agente</dt>
              <dd className="font-medium text-ink-900">{script.agentType}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Creado</dt>
              <dd className="text-ink-700">{formatDate(script.createdAt)}</dd>
            </div>
            {script.notes && (
              <div>
                <dt className="text-xs text-ink-400">Notas</dt>
                <dd className="whitespace-pre-wrap text-ink-700">{script.notes}</dd>
              </div>
            )}
          </dl>

          {/* Bidirectional visibility: deployments using any version of this script */}
          <h2 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-500">
            Deployments activos
          </h2>
          {deployments && deployments.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {deployments.map((d) => (
                <li key={d.deploymentId}>
                  <Link
                    to={`/super-admin/clients/${d.clientId}/deployment`}
                    className="font-medium text-moss hover:underline"
                  >
                    {d.clientName}
                  </Link>
                  <span className="ml-1 text-xs text-ink-400">usa v{d.scriptVersion}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-400">Ninguno</p>
          )}
        </div>
      </div>
    </div>
  );
}
